/**
 * Scan -> find -> keep. A scanned barcode is looked up in the user's own foods first (instant, works
 * for anything scanned before), then in the shared database, then in Open Food Facts. "Use this
 * food" saves it to My foods with its barcode and hands the food id to the caller, which shows the
 * usual amount picker. A product found nowhere can be added from photos of its package, and one
 * found with wrong or missing data can be fixed before it is used.
 */
import { useState, type ReactElement } from 'react';
import { errorMessage, log } from '../lib/logger';
import type { BarcodeFound, BarcodeLookupResponse, Food, FoodInsert } from '../lib/types';
import { useApp } from '../state/app';
import { LabelCheckLine } from './Badges';
import { BarcodeScanner } from './BarcodeScanner';
import { Icon } from './Icon';
import { formFromValues, LabelPhotoFlow } from './LabelEntry';
import { Spinner } from './ui';

type Step =
  | { kind: 'scan' }
  | { kind: 'looking'; gtin: string }
  | { kind: 'found'; result: BarcodeFound }
  | { kind: 'fix'; result: BarcodeFound }
  | { kind: 'missing'; gtin: string }
  | { kind: 'photos'; gtin: string }
  | { kind: 'error'; gtin: string; message: string };

const r0 = (x: number | undefined): string => (x === undefined ? '—' : Math.round(x).toString());
const r1 = (x: number | undefined): string => (x === undefined ? '—' : (Math.round(x * 10) / 10).toString());

/** Food row for a scanned product: label values per 100 g, one portion for the printed serving. */
export function foodFromScan(r: BarcodeFound): FoodInsert {
  const notes: string[] = [`Barcode ${r.gtin}. ${r.attribution}`];
  if (r.product.allergens.length > 0) notes.push(`Allergens: ${r.product.allergens.join(', ')}.`);
  if (r.product.ingredients) notes.push(`Ingredients: ${r.product.ingredients}`);
  return {
    source: 'barcode',
    barcode: r.gtin,
    name: r.product.name,
    brand: r.product.brand,
    category: r.product.categories[0]?.slice(0, 120) ?? null,
    nutrients: r.per100g,
    nutrient_sources: r.sources,
    portions: r.serving ? [{ label: r.serving.label ?? `1 serving (${r.serving.grams} g)`, grams: r.serving.grams }] : [],
    notes: notes.join(' ').slice(0, 2000),
    ai_model: null,
  };
}

function ProductCard({ r }: { r: BarcodeFound }): ReactElement {
  const s = r.perServing;
  const p = r.product;
  return (
    <div className="scan-product">
      {p.imageUrl !== null && <img src={p.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>{p.name}</div>
        <div className="faint" style={{ fontSize: 13, fontWeight: 600 }}>{[p.brand, p.quantity].filter(Boolean).join(' · ')}</div>
        <div className="row" style={{ gap: 6, marginTop: 6 }}>
          {p.nutriscore !== null && <span className={`chip nutri n-${p.nutriscore}`} title="Nutri-Score">Nutri-Score {p.nutriscore.toUpperCase()}</span>}
          {p.novaGroup !== null && <span className="chip" title="NOVA processing group (1 = unprocessed, 4 = ultra-processed)">NOVA {p.novaGroup}</span>}
          <span className="chip" title="Barcode">{r.gtin}</span>
        </div>
        {s !== null && r.serving !== null ? (
          <div className="macro-strip-sm num">
            <span><b>{r0(s.energy_kcal)}</b> kcal</span><span><b>{r1(s.protein_g)}</b> g protein</span><span><b>{r1(s.carbs_g)}</b> g carbs</span><span><b>{r1(s.fat_g)}</b> g fat</span>
            <span className="faint">per {r.serving.label ?? `${r.serving.grams} g`}</span>
          </div>
        ) : (
          <div className="macro-strip-sm num">
            <span><b>{r0(r.per100g.energy_kcal)}</b> kcal</span><span><b>{r1(r.per100g.protein_g)}</b> g protein</span><span><b>{r1(r.per100g.carbs_g)}</b> g carbs</span><span><b>{r1(r.per100g.fat_g)}</b> g fat</span>
            <span className="faint">per 100 g</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function BarcodeLookup({ onFood }: { onFood: (foodId: number) => void }): ReactElement {
  const { source, toast } = useApp();
  const [step, setStep] = useState<Step>({ kind: 'scan' });
  const [saving, setSaving] = useState<boolean>(false);

  const lookUp = async (gtin: string): Promise<void> => {
    setStep({ kind: 'looking', gtin });
    try {
      const known: Food | null = await source.findFoodByBarcode(gtin);
      if (known) {
        toast(known.owner_id === null ? `Found in the food database: ${known.brand ? `${known.brand} ` : ''}${known.name}` : `Found in your foods: ${known.name}`);
        onFood(known.id);
        return;
      }
      const res: BarcodeLookupResponse = await source.lookupBarcode(gtin);
      if (res.found) setStep({ kind: 'found', result: res });
      else setStep({ kind: 'missing', gtin: res.gtin });
    } catch (err) {
      log.warn('barcode.lookup_failed', { gtin, error: errorMessage(err) });
      setStep({ kind: 'error', gtin, message: errorMessage(err) });
    }
  };

  const keep = async (r: BarcodeFound): Promise<void> => {
    setSaving(true);
    try {
      const food: Food = await source.createFood(foodFromScan(r));
      toast(`Saved to My foods: ${food.name}`);
      onFood(food.id);
    } catch (err) {
      toast(errorMessage(err), 'err');
    } finally {
      setSaving(false);
    }
  };

  const again = (): void => setStep({ kind: 'scan' });

  if (step.kind === 'scan') return <BarcodeScanner onCode={(c) => void lookUp(c)} />;
  if (step.kind === 'looking') return <div className="row faint" style={{ padding: 24, justifyContent: 'center', fontWeight: 700 }}><Spinner /> Looking up {step.gtin}…</div>;
  if (step.kind === 'photos') {
    return <LabelPhotoFlow barcode={step.gtin} onSaved={(food) => onFood(food.id)} onCancel={again} />;
  }
  if (step.kind === 'fix') {
    const r: BarcodeFound = step.result;
    return (
      <div className="stack" style={{ gap: 12 }}>
        <ProductCard r={r} />
        <LabelPhotoFlow barcode={r.gtin} onSaved={(food) => onFood(food.id)} onCancel={() => setStep({ kind: 'found', result: r })}
          initial={formFromValues({ name: r.product.name, brand: r.product.brand, per100g: r.per100g, serving: r.serving, ingredients: r.product.ingredients, allergens: r.product.allergens })}
          correction={{
            ownFoodId: null, targetFoodId: null, barcode: r.gtin,
            original: { source: 'open_food_facts', name: r.product.name, brand: r.product.brand, serving_label: r.serving?.label ?? null, serving_grams: r.serving?.grams ?? null, nutrients: r.per100g },
          }} />
      </div>
    );
  }
  if (step.kind === 'missing' || step.kind === 'error') {
    return (
      <div className="stack" style={{ gap: 12 }}>
        <div className={`note ${step.kind === 'error' ? 'err' : 'warn'}`}>
          {step.kind === 'error' ? step.message : <>Barcode <b>{step.gtin}</b> isn’t in our database or Open Food Facts yet. Add it with a few photos of the package: next time it scans instantly, and with your OK it helps everyone else too.</>}
        </div>
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={() => setStep({ kind: 'photos', gtin: step.gtin })}><Icon name="camera" size={16} /> Add this product</button>
          <button type="button" className="btn" onClick={again}><Icon name="barcode" size={16} /> Scan another</button>
          {step.kind === 'error' && <button type="button" className="btn btn-ghost" onClick={() => void lookUp(step.gtin)}>Try again</button>}
        </div>
      </div>
    );
  }

  const r: BarcodeFound = step.result;
  return (
    <div className="stack" style={{ gap: 14 }}>
      <ProductCard r={r} />
      {r.warnings.length > 0 && <div className="note warn"><ul style={{ margin: 0 }}>{r.warnings.map((w) => <li key={w}>{w}</li>)}</ul></div>}
      <div className="row">
        <button type="button" className="btn btn-primary" disabled={saving || Object.keys(r.per100g).length === 0} onClick={() => void keep(r)}>
          {saving ? <Spinner /> : <Icon name="check" size={16} />} Use this food
        </button>
        <button type="button" className="btn" onClick={() => setStep({ kind: 'fix', result: r })}><Icon name="flag" size={16} /> Wrong or missing info? Fix it</button>
        <button type="button" className="btn btn-ghost" onClick={again}><Icon name="barcode" size={16} /> Scan another</button>
      </div>
      {r.labelCheck !== null
        ? <LabelCheckLine report={r.labelCheck.report} servingLabel={r.serving?.label ?? undefined} />
        : <p className="cite" style={{ margin: 0 }}>True Label check skipped: {r.labelCheckSkipped}</p>}
      <p className="cite" style={{ margin: 0 }}>{r.attribution} Carbohydrate read as {r.carbBasis === 'total' ? 'total (US label)' : 'available, with fiber added back (EU label)'}.</p>
    </div>
  );
}
